import cn from 'classnames';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import * as styles from './MultipinPreviewContainer.css';
import { MultipinPreviewError } from './components/MultipinPreviewError';
import { getMultipinSheetTitle } from './utils/getMultipinSheetTitle';
import { loadPreviewOffers } from '../../../../common/actions/previewOffers';
import { markPinAsViewed } from '../../../../common/actions/viewed/markPinAsViewed';
import { ModalHeader } from '../../../../common/components/ModalHeader';
import { ListingSkeleton } from '../../../../common/components/OfferCard/components/OfferCardSkeleton';
import { ETestId } from '../../../../common/constants';
import { OfferCardMobileContainer } from '../../../../common/containers/OfferCardMobileContainer';
import { PortalWrapper } from '../../../../common/containers/PortalWrapper';
import { ESwipeModalMode, SwipeModal } from '../../../../common/containers/SwipeModal';
import {
  selectPreviewOffers,
  selectPreviewOffersStatus,
  selectPreviewRealtyIds,
} from '../../../../common/selectors/previewOffers';
import { EFetchStatus } from '../../../../common/types/listingOffers';
import { TFeatureWithPrecision } from '../../../../common/types/map';
import { TThunkDispatch } from '../../../../common/types/redux';
import { getPositionInPin } from '../../../utils/getPositionInPin';

/** Сколько карточек-скелетонов показывать, пока объявления грузятся - как у листинга */
const SKELETON_CARDS_COUNT = 3;

export interface IMultipinPreviewContainerProps {
  feature: TFeatureWithPrecision;
  onClose(): void;
}

/** Шторка мультипина на телефоне: все объявления выбранного пина, полэкрана, свайп вверх раскрывает на весь. */
export const MultipinPreviewContainer = ({ feature, onClose }: IMultipinPreviewContainerProps) => {
  const dispatch = useDispatch<TThunkDispatch>();
  const offers = useSelector(selectPreviewOffers);
  const status = useSelector(selectPreviewOffersStatus);
  const realtyIds = useSelector(selectPreviewRealtyIds);
  const title = getMultipinSheetTitle(feature.properties);
  // Режим только у этой шторки: панель сверху при развороте не меняется (в отличие от листинга)
  const [mode, setMode] = React.useState(ESwipeModalMode.HALF);
  const isFull = mode === ESwipeModalMode.FULL;

  const handleExpand = React.useCallback(() => {
    setMode(ESwipeModalMode.FULL);
    // Просмотренность мультипина на телефоне - по развороту шторки, а не по тапу (см. openOfferPreview)
    dispatch(markPinAsViewed(feature));
  }, [dispatch, feature]);

  const handleRetry = React.useCallback(() => {
    dispatch(loadPreviewOffers(realtyIds));
  }, [dispatch, realtyIds]);

  const content = (() => {
    if (status === EFetchStatus.LOADING) {
      return <ListingSkeleton count={SKELETON_CARDS_COUNT} hasHeader={false} />;
    }

    if (status === EFetchStatus.FAILURE) {
      return <MultipinPreviewError onRetry={handleRetry} />;
    }

    // Те же карточки, что в списке: логику и поведение не меняем.
    // Бэк мог вернуть не все объявления пина - показываем, что есть.
    // Позиция для аналитики - в пине, а не в шторке: так же, как в событии выдачи по клику на пин.
    return (
      <div className={styles['offers']} data-testid={ETestId.MultipinPreviewOffers}>
        {offers.map((offer, index) => (
          <OfferCardMobileContainer
            key={offer.id}
            offer={offer}
            offerPosition={getPositionInPin(realtyIds, offer.id) ?? index + 1}
            offersLength={realtyIds.length}
            reason="pin_click"
          />
        ))}
      </div>
    );
  })();

  return (
    <PortalWrapper>
      <SwipeModal
        className={cn(styles['root'], isFull && styles['root_full'])}
        heightFromCss
        show
        mode={mode}
        header={<ModalHeader title={title} hasLargeTitle />}
        hasCloseButton
        testId={ETestId.MultipinPreview}
        onClose={onClose}
        onSwipeUp={handleExpand}
      >
        {content}
      </SwipeModal>
    </PortalWrapper>
  );
};

MultipinPreviewContainer.displayName = 'MultipinPreviewContainer';
